import { Retailer, RetailerAccount } from '@promoboxx/graphql-gateway-types'
import { useSelector } from 'react-redux'
import { Redirect, RouteChildrenProps, useParams } from 'react-router'

import campaignContentPaths from '@src/components/content/campaignContentPaths'
import industryContentPaths from '@src/components/content/industryContentPaths'
import NotFound from '@src/components/routes/NotFound'
import routePath from '@src/lib/routePath'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

import calendarPaths from '../calendar/calendarPaths'
import downloadablesPaths from '../downloadables/downloadablesPaths'
import reportingPaths from '../reporting/reportingPaths'
import RetailerAccountSelectorHandler from '../shared/RetailerAccountSelectorHandler/RetailerAccountSelectorHandler'

// HOC needed to ensure that currentRetailerAccountSelector works
const withRetailerAccountHandler =
  <T extends RouteChildrenProps>(
    Component: React.ComponentType<React.PropsWithChildren<T>>,
  ) =>
  (props: T) => (
    <RetailerAccountSelectorHandler>
      <Component {...props} />
    </RetailerAccountSelectorHandler>
  )

const LegacyBrandRoutesHandler: React.FC<
  React.PropsWithChildren<RouteChildrenProps>
> = (props) => {
  const search = new URLSearchParams(props.location?.search)
  const { retailerAccountSlug, brandSlug, ...rest } = useParams<{
    retailerAccountSlug: string
    brandSlug: string
  }>()

  let updatedRoute
  const retailerAccount: RetailerAccount = useSelector(
    currentRetailerAccountSelector,
  )

  const retailer = retailerAccount.retailers?.find(
    (retailer: Retailer) => String(retailer.brand?.slug) === brandSlug,
  )

  if (!retailerAccount || !retailer) {
    return <NotFound />
  }

  // Specific logic to handle new Industry Content routes and required filter param structure
  if (props.match?.path?.includes('/content')) {
    search.append('filter.content.organic_campaigns[brands][0]', retailer.id)
    search.append('info.content.organic_campaigns[offset]', '0')

    updatedRoute = `${
      retailer.brand?.community
        ? industryContentPaths.index.build({
            retailerAccountSlug,
            activeTab: 'organic',
            ...rest,
          })
        : campaignContentPaths.index.build({
            retailerAccountSlug,
            activeTab: 'organic',
            ...rest,
          })
    }?${search.toString()}`
  } else if (props.match?.path.includes('/downloadables')) {
    const params = props.match.params as ReturnType<
      (typeof downloadablesPaths)['index']['useParams']
    >
    search.append('filter.downloadables[brands][0]', retailer.brand?.id || '')
    search.append('info.downloadables[offset]', '0')

    updatedRoute = `${downloadablesPaths.index.build({
      retailerAccountSlug,
      brandSlug: 'all',
      parent_id: params.parent_id || 'root',
    })}?${search}`
  } else if (props.match?.path.includes('/schedule')) {
    search.append('filter.schedule[brands][0]', retailer.brand?.id || '')
    search.append('info.schedule[offset]', '0')

    updatedRoute = `${calendarPaths.index.build({
      retailerAccountSlug,
      brandSlug: 'all',
    })}?${search}`
  } else if (
    props.match?.path.includes('/reporting') &&
    // `.path` is the React Router path string, with placeholders. `.url` is the
    // actual url.
    props.match?.url.endsWith('/paid')
  ) {
    search.append(
      'filter.reporting.paid[retailerAccountId]',
      retailerAccount.id,
    )
    search.append('filter.reporting.paid[brands][0]', retailer.id)
    search.append('info.reporting.paid[offset]', '0')
    search.append(
      'info.reporting.paid[sort][sort_column]',
      'campaigns.start_date',
    )
    search.append('info.reporting.paid[sort][sort_direction]', 'desc')
    search.append('info.reporting.paid[page]', '1')

    updatedRoute = `${reportingPaths.index.build({
      retailerAccountSlug,
      campaignType: 'paid',
    })}?${search}`
  } else if (
    props.match?.path.includes('/reporting') &&
    props.match?.url.endsWith('/organic')
  ) {
    search.append('filter.reporting.organic[brands][0]', retailer.id)
    search.append(
      'filter.reporting.organic[retailerAccountId]',
      retailerAccount.id,
    )
    search.append('info.reporting.organic[offset]', '0')
    search.append(
      'info.reporting.organic[sort][sort_column]',
      'campaigns.start_date',
    )
    search.append('info.reporting.organic[sort][sort_direction]', 'desc')
    search.append('info.reporting.organic[pageSize]', '10')
    search.append('info.reporting.organic[page]', '1')

    updatedRoute = `${reportingPaths.index.build({
      retailerAccountSlug,
      campaignType: 'organic',
    })}?${search}`
  } else if (props.match?.path) {
    search.append('brandSlug', brandSlug)
    updatedRoute = `${routePath(props.match.path).build({
      retailerAccountSlug,
      brandSlug: 'all',
      ...rest,
    })}?${search.toString()}`
  }

  return updatedRoute ? <Redirect to={updatedRoute} /> : <NotFound />
}

export default withRetailerAccountHandler(LegacyBrandRoutesHandler)
